import React, { FC } from 'react';
import { useQuery } from '@apollo/client/react';
import { SemibigHeader } from '../../../ui/Header/Header';
import {
  DisplayDateRange,
  GetTranslationsQueryVariables,
  Query,
  NarvarStatusCode,
  QueryFormatEddArgs,
  FormatBlockEddQuery,
} from '../../../../../../__generated__/loma';
import { renderTemplate } from '../utils';
import { Clock } from './Icons';
import { useTheme } from '../../../../hooks/useTheme';
import translationsQueryByKey from '../../../../../../graphql/translationsQueryByKey';
import getFormattedEdd from '../../../../../../graphql/getFormattedEdd';
import { usePageContext } from '../../../../hooks/PageContext/usePageContext';
import { isEddToday } from '../../../../lib/eddTimezone';
import styles from './Status.module.css';

interface StatusProps {
  displayDate: string;
  displayDateRange?: DisplayDateRange | null;
  status?: string;
  statusCode?: NarvarStatusCode;
  subStatus?: NarvarStatusCode | null;
}

const Status: FC<StatusProps> = (props) => {
  const { displayDate, displayDateRange, status, statusCode, subStatus } =
    props;

  const { state } = usePageContext();
  const {
    retailerMoniker,
    localeLanguage,
    localeCountry,
    versionName,
    type,
    cache,
  } = state;

  const stringifiedKeys = ['blockDeliveryStatusEtaText', 'trackingStatus'].join(
    '\n',
  );

  const { data: translationsData } = useQuery<
    Pick<Query, 'retailerTranslations'>,
    GetTranslationsQueryVariables
  >(translationsQueryByKey(stringifiedKeys), {
    skip: !statusCode && !status,
    variables: {
      retailerMoniker,
      localeLanguage: localeLanguage || 'en',
      localeCountry,
      versionName,
      type,
      category: subStatus ?? statusCode,
      status,
      cache,
    },
  });

  const { blockDeliveryStatusEtaText, trackingStatus } =
    translationsData?.retailerTranslations ?? {};

  const { bodytextDefaultNone } = useTheme();
  const eddFormatTranslationKeys: QueryFormatEddArgs['eddCombos'][0]['eddFormatTranslationKeys'] =
    [
      {
        sameDay: 'blockDeliveryStatusEtaFormattedTime',
        lastDay: 'blockDeliveryStatusEtaFormattedTime',
        nextDay: 'blockDeliveryStatusEtaFormattedTime',
        lastWeek: 'blockDeliveryStatusEtaFormattedTime',
        nextWeek: 'blockDeliveryStatusEtaFormattedTime',
        sameElse: 'blockDeliveryStatusEtaFormattedTime',
      },
      {
        sameDay: 'blockEddMonthSameDayDateFormat',
        lastDay: 'blockEddMonthLastDayDateFormat',
        nextDay: 'blockEddMonthNextDayDateFormat',
        lastWeek: 'blockEddMonthLastWeekDateFormat',
        nextWeek: 'blockEddMonthNextWeekDateFormat',
        sameElse: 'blockEddMonthSameElseDateFormat',
      },
      {
        sameDay: 'blockEddDayOfMonthSameDayDateFormat',
        lastDay: 'blockEddDayOfMonthLastDayDateFormat',
        nextDay: 'blockEddDayOfMonthNextDayDateFormat',
        lastWeek: 'blockEddDayOfMonthLastWeekDateFormat',
        nextWeek: 'blockEddDayOfMonthNextWeekDateFormat',
        sameElse: 'blockEddDayOfMonthSameElseDateFormat',
      },
    ];

  const hasEddData =
    !!displayDate || !!displayDateRange?.begin || !!displayDateRange?.end;

  const { data } = useQuery<FormatBlockEddQuery, QueryFormatEddArgs>(
    getFormattedEdd,
    {
      variables: {
        retailerMoniker,
        versionName,
        localeLanguage,
        localeCountry,
        eddCombos: [
          { displayDate, eddFormatTranslationKeys, dateOnly: false },
          {
            displayDate: displayDateRange?.begin || '',
            eddFormatTranslationKeys,
            dateOnly: false,
          },
          {
            displayDate: displayDateRange?.end || '',
            eddFormatTranslationKeys,
            dateOnly: false,
          },
        ],
      },
      skip: !hasEddData,
    },
  );

  const { formatEdd } = data ?? {};

  const [
    displayDateFormatted = [],
    displayDateRangeBeginFormatted = [],
    displayDateRangeEndFormatted = [],
  ] = formatEdd?.formattedEdds || [];

  const trackingStatusText = renderTemplate(trackingStatus ?? '', {
    arrival: { minutes: '28' },
  });

  const buildEddText = (formatted: string[] = [], iso?: string | null) => {
    const [time = '', month = '', dayOfMonth = ''] = formatted;
    if (!time) return '';
    if (isEddToday(iso)) return time;

    const date = [month, dayOfMonth].filter(Boolean).join(' ');
    return date ? `${date}, ${time}` : time;
  };

  const datePart = (iso?: string | null) => iso?.split('T')[0];
  const rangeSpansSingleDay =
    !!displayDateRange?.begin &&
    datePart(displayDateRange.begin) === datePart(displayDateRange.end);

  const displayDateText = buildEddText(displayDateFormatted, displayDate);
  const displayDateRangeBeginText = buildEddText(
    displayDateRangeBeginFormatted,
    displayDateRange?.begin,
  );
  const displayDateRangeEndText = rangeSpansSingleDay
    ? (displayDateRangeEndFormatted[0] ?? '')
    : buildEddText(displayDateRangeEndFormatted, displayDateRange?.end);

  const eta = renderTemplate(blockDeliveryStatusEtaText ?? '', {
    arrival: {
      begin: displayDateRangeBeginText,
      end: displayDateRangeEndText,
      time: displayDateText,
    },
    delivery: {
      time: displayDateText,
    },
    pickup: {
      begin: displayDateRangeBeginText,
      end: displayDateRangeEndText,
      time: displayDateText,
    },
  });

  const showETA =
    displayDateText || displayDateRangeBeginText || displayDateRangeEndText;

  return (
    <div>
      <SemibigHeader as="h2">{trackingStatusText}</SemibigHeader>
      {showETA && (
        <div className={styles.eddContainer}>
          <div className={styles.clockIcon}>
            <Clock />
          </div>
          <div className={styles.statusText}>
            <span style={{ ...bodytextDefaultNone }}>{eta}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Status;
